// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { useLocalMessage } from '@Helpers/';
import { TLPValueArray, TLPValues, PAPValueArray, PAPValues } from 'thehive-sdk';
import { FormInstance } from 'antd/lib/form';
import { BulkEditFormField } from '@Molecules/BulkEditFormField/BulkEditFormField';
import { SecurityInput } from '@Atoms/Inputs/SecurityInput/SecurityInput';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';

import './ObservableBulkEditForm.scss';

export interface ObservableBulkEditValues extends Store {
    tlp: TLPValues;
    pap: PAPValues;
    isIoc: boolean;
    hasBeenSighted: boolean;
    ignoreSimilarity: boolean;
    addTags: string[];
    removeTags: string[];
}

interface ObservableBulkEditFormProps {
    form: FormInstance;
    isValid: boolean;
    onFinish: (values: ObservableBulkEditValues) => void;
    onChange: () => void;
    onCancel: () => void;
}

export const ObservableBulkEditForm = ({
    form,
    isValid,
    onFinish,
    onChange,
    onCancel,
}: ObservableBulkEditFormProps): ReactElement => {
    const i = useLocalMessage();

    const onValuesChange = (changedValue: Store): void => {
        const valueName = Object.keys(changedValue)[0] || '';

        if (!valueName.includes('Checked')) {
            form.setFieldsValue({ [`${valueName}Checked`]: true });
        }
        onChange();
    };

    return (
        <THForm<ObservableBulkEditValues>
            name="observable-bulk-edit-form"
            onValuesChange={onValuesChange}
            form={form}
            onFinish={onFinish}
            initialValues={{ tlp: 2, ioc: false, sighted: false, ignoreSimilarity: false }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i(`general.submit`)} />}
            >
                <BulkEditFormField
                    label={i('common.tlp')}
                    name="tlp"
                    input={<SecurityInput type="tlp" valueArray={TLPValueArray} />}
                />

                <BulkEditFormField
                    label={i('common.pap')}
                    name="pap"
                    input={<SecurityInput type="pap" valueArray={PAPValueArray} />}
                />

                <BulkEditFormField label={i('observable.create.form.ioc.label')} name="ioc" input={<THSwitch />} />

                <BulkEditFormField
                    label={i('observable.create.form.sighted.label')}
                    name="sighted"
                    input={<THSwitch />}
                />

                <BulkEditFormField
                    label={i('observable.create.form.similarity.label')}
                    name="ignoreSimilarity"
                    input={<THSwitch />}
                />

                <BulkEditFormField
                    label={i('observable.bulk-edit.form.add-tags.label')}
                    name="addTags"
                    input={<THTagSelector.Autocomplete />}
                />

                <BulkEditFormField
                    label={i('observable.bulk-edit.form.remove-tags.label')}
                    name="removeTags"
                    input={<THTagSelector.Autocomplete />}
                />
            </THForm.Layout>
        </THForm>
    );
};
